import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { styled, Box, CircularProgress } from "@mui/material";

import {
  Header,
  Hero,
  FeatureCardsSection,
  SecuritySection,
  FeatureExplanationSection,
  FAQSection,
  Footer,
} from "./components";
import { AuthFeedbackModal } from "@components/Modals/AuthFeedbackModal";
import { useAuth } from "@contexts";

const LandingContainer = styled("div")({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  width: "100%",
});

const FirstSectionContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  background: `linear-gradient(180deg, #DFD8FF 0%, #F0EDFC 71%, #FFFFFF 100%)`,
  justifyContent: "center",
  width: "100%",
  minHeight: "636px",
  [theme.breakpoints.up("sm")]: {
    minHeight: "540px",
  },
  [theme.breakpoints.up("lg")]: {
    minHeight: "835px",
  },
  [theme.breakpoints.up("xl")]: {
    minHeight: "881px",
  },
}));

const FirstSectionContent = styled(Box)({
  display: "flex",
  flexDirection: "column",
  maxWidth: "1536px",
  flex: 1,
  padding: "32px 5% 0",
});

export const LandingPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const {
    magicLinkSignIn,
    authChallengeFeedback: { message: authFeedback, authStep },
  } = useAuth();

  const [usingMagicLink, setUsingMagicLink] = useState(false);
  const [searchParams] = useSearchParams();
  const userEmail = searchParams.get("email");
  const authCode = searchParams.get("code");

  const feedbackIcon = () => {
    return <CircularProgress size={24} color="primary" />;
  };

  useEffect(() => {
    async function signInWithMagicLink() {
      if (userEmail && authCode) {
        setUsingMagicLink(true);

        const magicLinkLoginSuccess = await magicLinkSignIn(userEmail, authCode);

        if (magicLinkLoginSuccess) {
          navigate("/app/solicitudes");
        } else {
          navigate("/app");
        }

        setUsingMagicLink(false);
      }
    }

    signInWithMagicLink();
  }, [userEmail, authCode, magicLinkSignIn, navigate]);

  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (location.pathname !== "/") {
      modalRef.current?.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [location]);

  return (
    <LandingContainer>
      <FirstSectionContainer>
        <FirstSectionContent>
          <Header />
          <Hero />
        </FirstSectionContent>
      </FirstSectionContainer>
      <FeatureCardsSection />
      <SecuritySection />
      <FeatureExplanationSection />
      <FAQSection />
      <Footer />

      {usingMagicLink && (
        <AuthFeedbackModal
          isOpen={usingMagicLink}
          authFeedback={authFeedback}
          authStep={authStep}
          feedbackIcon={feedbackIcon}
        />
      )}
    </LandingContainer>
  );
};
